import React, { useContext, useState } from 'react';
import { SxProp, View, Image } from 'dripsy';

import { TextView } from '@/design-system/components/typography';
import { CurrenciesContext } from '../../../packages/currencies/currency.context';
import { Icon } from '@/design-system/components/icons/Icon';
import { SkeletonView } from '@/design-system/components/cards';

export enum CurrencyIconVariant {
  default = 'default',
  tableItemRow = 'tableItemRow',
  native = 'native',
  large = 'large',
  dropdown = 'dropdown',
  activityHistoryNative = 'activityHistoryNative'
}

interface AssetCardDefaultProps {
  asset: string;
  title?: string;
  subText?: string;
  textSize?: 'textBody' | 'H6' | 'textTitle';
  subTextSize?: 'textCaption' | 'textBody';
  textBold?: boolean;
  textSemiBold?: boolean;
  sx?: SxProp;
  textSx?: SxProp;
  subTextSx?: SxProp;
  variant?: CurrencyIconVariant;
  hideText?: boolean;
  loading?: boolean;
}

export type AssetCardProps = AssetCardDefaultProps;

export const CurrenciesIcon: React.FC<AssetCardProps> = ({
  asset,
  title,
  subText,
  textSize,
  subTextSize,
  textBold = true,
  textSemiBold = false,
  sx,
  textSx,
  subTextSx,
  variant = CurrencyIconVariant.default,
  hideText,
  loading = false
}) => {
  const [error, setError] = useState<boolean>(false);
  const { currenciesDict } = useContext(CurrenciesContext);
  const uri = currenciesDict?.[asset]?.token_image_url;

  const ICON_STYLE = {
    default: sxStyles.iconDefault,
    tableItemRow: sxStyles.iconTableItemRow,
    native: sxStyles.iconNative,
    large: sxStyles.iconLarge,
    dropdown: sxStyles.dropdown,
    activityHistoryNative: sxStyles.activityHistoryNative
  };

  if (loading) {
    return (
      <View sx={{ ...sxStyles.container, ...sx }}>
        <View
          sx={{
            ...sxStyles.containerLeft
          }}>
          <View sx={{ height: '$10', width: '$10', backgroundColor: '$disable', borderRadius: 100 }} />
        </View>
        <View
          sx={{
            ...sxStyles.containerRight
          }}>
          <SkeletonView sx={{ width: 80, height: 20, borderRadius: 5 }} />
          <SkeletonView sx={{ width: 100, height: 20, borderRadius: 5, marginTop: 5 }} />
        </View>
      </View>
    );
  }

  return (
    <View sx={{ ...sxStyles.container, ...sx }}>
      <View sx={sxStyles.containerLeft}>
        <View sx={sxStyles.iconContainer}>
          {(!uri || error) && <Icon name={asset.toLowerCase()} height={30} width={30} />}
          {uri && !error && (
            <Image
              source={{
                uri
              }}
              sx={ICON_STYLE[variant]}
              onError={() => setError(true)}
            />
          )}
        </View>
      </View>
      {!hideText && (
        <View sx={sxStyles.containerRight}>
          <TextView variant={textSize || 'H6'} bold={textBold} semiBold={textSemiBold} label={!!subText} sx={textSx}>
            {title || asset}
          </TextView>
          {subText && (
            <TextView secondary variant={subTextSize || 'textCaption'} sx={subTextSx}>
              {subText}
            </TextView>
          )}
        </View>
      )}
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    // flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  containerLeft: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  containerRight: {
    marginLeft: '$3',
    flexDirection: 'column'
  },
  iconContainer: {
    borderRadius: 100,
    borderWidth: 1,
    borderColor: '$borderPrimary'
  },
  iconDefault: { height: '$7', width: '$7' },
  iconTableItemRow: { height: '$5', width: '$5' },
  iconNative: { height: 30, width: 30 },
  dropdown: { height: '$5', width: '$5' },
  iconLarge: { height: 40, width: 40 },
  activityHistoryNative: { height: '$7', width: '$7' }
};
